import { useCallback, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useLanguage } from "@/i18n/LanguageProvider";
import { prepareNutritionPhoto } from "@/lib/nutritionPhoto";

/**
 * Photo logging hook — invokes the nutrition-photo-analyze edge fn with
 * Claude Sonnet 4.5 vision and returns the parsed meal analysis.
 *
 * Quota (decisão 23/07): o SERVIDOR é a única fonte de verdade — Free tem
 * 1 análise VITALÍCIA, depois créditos (débito automático). O antigo gate
 * client-side de 3 fotos/dia foi removido: contradizia o modelo vitalício
 * (bloquearia um Free COM créditos) e era contornável por script.
 *
 * Erros específicos que o servidor devolve e a UI trata:
 *   - 402 { error: "photo_quota_exhausted" }  → "free_photo_used"
 *     (gratuita usada E sem créditos → CTA Premium / comprar créditos)
 *   - 429 { error: "rate_limited", scope: "global" } → "photo_daily_cap"
 *     (backstop global do dia — tenta amanhã)
 */

export interface PhotoAnalysisResult {
  description: string;
  kcal: number;
  protein: number;
  carbs: number;
  fat: number;
  confidence: number;
  ingredients: string[];
  portion_estimate: string;
  meal_type_guess: string;
}

export type PhotoAnalyzeErrorCode =
  | "not_authenticated"
  | "free_photo_used"
  | "photo_daily_cap"
  | "api_error"
  | "malformed_response"
  | "unexpected_error";

function localeForLang(lang: string): "pt-BR" | "en" | "nl" {
  if (lang === "EN") return "en";
  if (lang === "NL") return "nl";
  return "pt-BR";
}

// Lê o body JSON de um FunctionsHttpError (mesmo padrão tolerante do
// parsePaymentErrorFromInvokeError em src/lib/quotaError.ts: context é
// normalmente a Response crua, mas tolera objeto já parseado).
async function readInvokeErrorBody(err: unknown): Promise<Record<string, unknown> | null> {
  const ctx = (err as { context?: unknown } | null | undefined)?.context;
  if (!ctx) return null;
  const maybeRes = ctx as { json?: () => Promise<unknown>; clone?: () => Response };
  try {
    if (typeof maybeRes.json === "function") {
      const src = typeof maybeRes.clone === "function" ? maybeRes.clone() : maybeRes;
      const body = await (src as { json: () => Promise<unknown> }).json();
      return body && typeof body === "object" ? (body as Record<string, unknown>) : null;
    }
    return typeof ctx === "object" ? (ctx as Record<string, unknown>) : null;
  } catch {
    return null;
  }
}

export function useNutritionPhotoAnalyze() {
  const { lang } = useLanguage();
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [error, setError] = useState<PhotoAnalyzeErrorCode | null>(null);
  const [lastResult, setLastResult] = useState<PhotoAnalysisResult | null>(null);

  const analyzePhoto = useCallback(
    async (imageBase64: string): Promise<{ result?: PhotoAnalysisResult; error?: PhotoAnalyzeErrorCode }> => {
      setError(null);
      setIsAnalyzing(true);

      const fail = (code: PhotoAnalyzeErrorCode) => {
        setIsAnalyzing(false);
        setError(code);
        return { error: code };
      };

      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return fail("not_authenticated");

        const preparedImage = await prepareNutritionPhoto(imageBase64);

        // ── Invoke edge fn (servidor decide cota vitalícia/créditos) ────
        const { data, error: invokeErr } = await supabase.functions.invoke(
          "nutrition-photo-analyze",
          {
            body: {
              image_base64: preparedImage,
              locale: localeForLang(lang),
            },
          },
        );

        if (invokeErr) {
          const body = await readInvokeErrorBody(invokeErr);
          if (body?.error === "photo_quota_exhausted") {
            return fail("free_photo_used");
          }
          if (body?.error === "rate_limited" && body?.scope === "global") {
            return fail("photo_daily_cap");
          }
          console.error("[useNutritionPhotoAnalyze] invoke failed:", invokeErr);
          return fail("api_error");
        }

        const result = data as PhotoAnalysisResult;
        if (
          !result ||
          typeof result.description !== "string" ||
          typeof result.confidence !== "number"
        ) {
          return fail("malformed_response");
        }

        setLastResult(result);
        setIsAnalyzing(false);
        return { result };
      } catch (e) {
        console.error("[useNutritionPhotoAnalyze] threw:", e);
        return fail("unexpected_error");
      }
    },
    [lang],
  );

  return { analyzePhoto, isAnalyzing, error, lastResult };
}
